import { useState, useRef, useEffect, useCallback } from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Link } from "react-router-dom"
import { useMutation } from "@tanstack/react-query"
import { ErrorCard } from "@/components/ui/error"
import { ROUTES } from "@/routes/path"
import { confirmRegistration, resendConfirmation } from "@/lib/http/auth"
import { useToast } from "@/hooks/useToast"
import { useOnAuthSuccess } from "@/hooks/useAuth"
import { useCheckPendingInvitations } from "@/hooks/useTeam"

import { useAuth } from "@/hooks/useAuth"

const OTP_LENGTH = 6
const COUNTDOWN_SECONDS = 60

export default function ConfirmPage() {
  const { toast } = useToast()
  const { email } = useAuth()
  const onAuthSuccess = useOnAuthSuccess()
  const checkPendingInvitations = useCheckPendingInvitations()

  const [code, setCode] = useState<string[]>(Array(OTP_LENGTH).fill(""))
  const [activeIndex, setActiveIndex] = useState(0)
  const [formError, setFormError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const [shake, setShake] = useState(false)
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS)
  const [isResendDisabled, setIsResendDisabled] = useState(true)

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, OTP_LENGTH)
    inputRefs.current[0]?.focus()
  }, [])

  useEffect(() => {
    if (!isResendDisabled) return

    if (countdown === 0) {
      setIsResendDisabled(false)
      return
    }

    const timerId = setInterval(() => {
      setCountdown((prev) => prev - 1)
    }, 1000)

    return () => clearInterval(timerId)
  }, [countdown, isResendDisabled])

  const handleResendCode = () => {
    resendConfirmation(email!).then(() => {
      toast({
        title: "OTP resent",
        description: "Check your email for the new code.",
        variant: "success",
      })
      setIsResendDisabled(true)
      setCountdown(COUNTDOWN_SECONDS)
    })
  }

  const mutation = useMutation({
    mutationFn: async (token: string) => {
      setIsLoading(true)
      setFormError(null)
      return await confirmRegistration(email!, token)
    },
    onSuccess: async (data) => {
      if (data?.token) {
        // Check for pending invitations before proceeding with normal auth flow
        const hasPendingInvitation = await checkPendingInvitations(email!, { token: data.token, user: data })
        if (!hasPendingInvitation) {
          onAuthSuccess(data)
        }
        return
      }
      setFormError("Invalid verification code.")
    },
    onError: (error: any) => {
      setShake(true)
      setTimeout(() => setShake(false), 600)
      setFormError(error?.response?.data?.error || "Invalid verification code.")
    },
    onSettled: () => {
      setIsLoading(false)
    },
  })

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value.slice(-1)
    setCode(newCode)

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
      setActiveIndex(index + 1)
    }

    if (newCode.every((digit) => digit !== "")) {
      mutation.mutate(newCode.join(""))
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
      setActiveIndex(index - 1)
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus()
      setActiveIndex(index - 1)
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
      setActiveIndex(index + 1)
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData("text/plain").slice(0, OTP_LENGTH)

    if (!/^\d+$/.test(pastedData)) {
      setShake(true)
      setTimeout(() => setShake(false), 600)
      return
    }

    const newCode = [...code]
    pastedData.split("").forEach((digit, index) => {
      newCode[index] = digit
    })
    setCode(newCode)

    const focusIndex = Math.min(pastedData.length, OTP_LENGTH - 1)
    inputRefs.current[focusIndex]?.focus()
    setActiveIndex(focusIndex)

    if (newCode.every((digit) => digit !== "")) {
      mutation.mutate(newCode.join(""))
    }
  }

  const setInputRef = useCallback(
    (index: number) => (el: HTMLInputElement | null) => {
      inputRefs.current[index] = el
    },
    []
  )

  if (!email) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p>Email not found. Please go back to the registration page.</p>
      </div>
    )
  }

  return (
    <div className="relative flex h-screen flex-col items-center justify-center bg-gray-50 dark:bg-zinc-900">
      <div className="absolute left-1/2 top-10 -translate-x-1/2">
        <img src="/logo.svg" alt="Ledgerbeam" width={220} height={50} />
      </div>

      <div className="w-full max-w-md space-y-6 rounded-xl bg-white p-8 text-center shadow-xl dark:bg-zinc-800">
        <h1 className="text-2xl font-semibold dark:text-zinc-100">Check your email</h1>
        <p className="text-muted-foreground">
          We&apos;ve sent a six-digit confirmation code to <span className="font-semibold text-primary">{email}</span>.{" "}
          <Link to={ROUTES.AUTH.REGISTER} className="text-sm text-primary hover:underline">
            Change email
          </Link>
        </p>

        <fieldset disabled={isLoading} className={cn("flex flex-col gap-4", shake && "animate-shake")}>
          <div className="flex items-center justify-center gap-2">
            {Array.from({ length: OTP_LENGTH }).map((_, index) => (
              <input
                key={`otp-input-${index}`}
                ref={setInputRef(index)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={code[index]}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                onFocus={() => setActiveIndex(index)}
                className={cn(
                  "size-14 text-center text-lg font-semibold",
                  "rounded-lg border",
                  "transition-all duration-200",
                  "bg-white dark:bg-zinc-900",
                  "border-zinc-200 dark:border-zinc-700",
                  "focus:outline-hidden focus:ring-2 focus:ring-primary/20",
                  activeIndex === index && "border-primary dark:border-primary"
                )}
              />
            ))}
          </div>
          {formError && <ErrorCard message={formError} />}
        </fieldset>
        <p className="text-sm text-muted-foreground">
          Didn&apos;t receive a code?{" "}
          <Button variant="link" size="sm" className="px-0.5" onClick={handleResendCode} disabled={isResendDisabled}>
            {isResendDisabled
              ? `Resend in ${String(Math.floor(countdown / 60)).padStart(2, "0")}:${String(countdown % 60).padStart(2, "0")}`
              : "Resend code"}
          </Button>
        </p>
      </div>

      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 text-center text-sm text-muted-foreground">
        <Link to={ROUTES.AUTH.LOGIN}>&larr; Back to login</Link>
      </div>
    </div>
  )
}
